import { useState, useEffect } from "react";
import {
  FiberManualRecordRounded,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
} from "@material-ui/icons";
import styled from "styled-components";
import { colors } from "../../res/values/values";

const SLIDE_DURATION = 4000;
const BANNER_TYPES = {
  home: "HOME",
  left: "LEFT",
  bottom: "BOTTOM BANNER",
};

//#region Styles
const Container = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 1280px;
  margin-bottom: ${(props) => (props.type==='bottom' ? 2.8 : 0)}rem;
`;

// Every slide sits in the same grid cell, so the container takes the height of the
// tallest loaded image and slides can cross-fade without absolute positioning.
const Slides = styled.div`
  display: grid;
`;

const Image = styled.img`
  grid-area: 1 / 1;
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  cursor: pointer;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  visibility: ${(props) => (props.$active ? "visible" : "hidden")};
  transition: opacity 0.6s ease, visibility 0.6s;
`;
const RightButton = styled.div`
  position: absolute;
  color: ${colors.primaryColor};
  top: ${(props) => props.heightPosition}%;
  right: ${(props) => props.widthPosition}%;
  border-radius: 50%;
  box-shadow: 2px 5px 10px rgba(0, 0, 0, 0.4);
  height: 35px;
  cursor: pointer;
  z-index: 1;
`;
const LeftButton = styled.div`
  position: absolute;
  color: ${colors.primaryColor};
  top: ${(props) => props.heightPosition}%;
  left: ${(props) => props.widthPosition}%;
  border-radius: 50%;
  box-shadow: 2px 5px 10px rgba(0, 0, 0, 0.4);
  height: 35px;
  cursor: pointer;
  z-index: 1;
`;
const Buttons = styled.div`
  position: absolute;
  bottom: 0%;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-direction: row;
  display: flex;
  z-index: 1;
`;
const Button = styled.div`
  color: ${(props) => (props.color === "black" ? colors.primaryColor : "white")};
  cursor: pointer;
`;
//#endregion

function ImageSlider(props) {
  const [bannerImages, setBannerImages] = useState([]);
  // Index the user / timer asked for (drives the dots).
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Index actually on screen; only switches once that image has finished loading,
  // so a slow image never shows half-drawn or leaves the previous banner under the new dot.
  const [shownIndex, setShownIndex] = useState(0);
  // Loaded state is keyed by banner id so it survives a broken banner being dropped.
  const [loaded, setLoaded] = useState({});

  // Fetch banners once per slider type.
  useEffect(() => {
    let isMounted = true;
    fetch(`${process.env.REACT_APP_API}/banners`)
      .then((res) => res.json())
      .then((res) => {
        if (!isMounted) return;
        const filteredList = res.message.data
          .filter((x) => x.bannertype === BANNER_TYPES[props.type])
          .sort((a, b) => a.sortorder - b.sortorder);
        setBannerImages(filteredList);
        setSelectedIndex(0);
        setShownIndex(0);
        setLoaded({});
      })
      .catch((err) => console.error("Banners could not be loaded", err));
    return () => {
      isMounted = false;
    };
  }, [props.type]);

  // Keep indexes in range after a broken banner is removed.
  useEffect(() => {
    if (bannerImages.length === 0) return;
    if (selectedIndex >= bannerImages.length) setSelectedIndex(0);
    if (shownIndex >= bannerImages.length) setShownIndex(0);
  }, [bannerImages.length, selectedIndex, shownIndex]);

  const isLoaded = (index) => !!(bannerImages[index] && loaded[bannerImages[index].id]);

  useEffect(() => {
    if (isLoaded(selectedIndex)) setShownIndex(selectedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, loaded, bannerImages]);

  // Auto-advance. Restarts whenever the slide changes (also after a manual click) and
  // waits until both the current and the next image are loaded.
  const nextIndex = bannerImages.length > 0 ? (selectedIndex + 1) % bannerImages.length : 0;
  const canAdvance = bannerImages.length > 1 && selectedIndex === shownIndex && isLoaded(nextIndex);
  useEffect(() => {
    if (!canAdvance) return;
    const id = setTimeout(() => setSelectedIndex(nextIndex), SLIDE_DURATION);
    return () => clearTimeout(id);
  }, [canAdvance, nextIndex]);

  const rightSide = () => {
    if (bannerImages.length > 0) setSelectedIndex(nextIndex);
  };
  const leftSide = () => {
    if (bannerImages.length > 0)
      setSelectedIndex((selectedIndex - 1 + bannerImages.length) % bannerImages.length);
  };
  const handleImageLoad = (id) => {
    setLoaded((prev) => ({ ...prev, [id]: true }));
  };
  // A banner whose file is missing is dropped instead of showing a broken image.
  const handleImageError = (id) => {
    setBannerImages((prev) => prev.filter((b) => b.id !== id));
  };
  const handleClick = (myLink) => {
    //if(myLink!=='') window.location.href=myLink;
  }

  return (
    <Container type={props.type}>
      {bannerImages.length > 0 && (
        <>
          <Slides>
            {bannerImages.map((banner, index) => (
              <Image
                key={banner.id}
                $active={index === shownIndex}
                alt={banner.title}
                src={`/${banner.link}`}
                onLoad={() => handleImageLoad(banner.id)}
                onError={() => handleImageError(banner.id)}
                onClick={(_) => handleClick(`${banner.url}`)}
              />
            ))}
          </Slides>
          {props.type === "home" && bannerImages.length > 1 && (
            <>
              <LeftButton onClick={leftSide} heightPosition={40} widthPosition={1}>
                <KeyboardArrowLeftOutlined fontSize="large" />
              </LeftButton>
              <RightButton onClick={rightSide} heightPosition={40} widthPosition={1}>
                <KeyboardArrowRightOutlined fontSize="large" />
              </RightButton>
              <Buttons>
                {bannerImages.map((banner, index) => (
                  <Button
                    key={banner.id}
                    onClick={() => setSelectedIndex(index)}
                    color={index === selectedIndex ? "black" : "white"}
                  >
                    <FiberManualRecordRounded style={{width:"1rem"}} />
                  </Button>
                ))}
              </Buttons>
            </>
          )}
        </>
      )}
    </Container>
  );
}

export default ImageSlider;
